import { useRef, useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  Chip,
  IconButton,
  Divider,
  Button,
  CircularProgress,
} from '@mui/material';
import Alert from '@mui/material/Alert';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockIcon from '@mui/icons-material/Lock';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import { useTraining } from '../../context/TrainingContext';
import { ArrowForward, Cancel, Pending, Quiz } from '@mui/icons-material';
import axios from 'axios';

const TrainingModule = () => {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const { getAlert, setGetAlert, modules, getTrainingModules, updateProgressOnBackend } = useTraining();

  const videoRef = useRef(null);
  const [alert, setAlert] = useState(null);
  const [videoDuration, setVideoDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);

  const maxReachedRef = useRef(0);
  const isCorrecting = useRef(false);
  const currentTraining = modules?.trainings?.find(
    (t) => String(t.id) === String(moduleId)
  );
  const examStatus = modules?.exam_status || currentTraining?.exam_status;
  const startAt = currentTraining?.exam_startat || modules?.exam_startat || currentTraining?.exam_start_at || modules?.exam_start_at;

  // Fetch modules if not loaded
  useEffect(() => {

    if (!modules || !modules.trainings) {
      getTrainingModules();
    }
  }, [modules, getTrainingModules]);

  // Reset initialization flag when moduleId changes
  useEffect(() => {

    setIsInitialized(false);
    maxReachedRef.current = 0;
    setVideoDuration(0);
    setCurrentTime(0);
  }, [moduleId]);

  const handleProceedToAssessment = async () => {

    if (examStatus === "In Progress") {
      if (startAt) {
        const startTime = new Date(startAt).getTime();

        //one hour exam time
        const endTime = startTime + 60 * 60 * 1000;
        if (!isNaN(startTime) && Date.now() >= endTime) {
          setAlert({ type: 'error', message: "The exam has ended." });
          return;
        }
      }
      navigate(`/app/training/${moduleId}/assessment`);
    } else if (examStatus === "Not Completed" || !examStatus) {
      try {
        const response = await axios.put(
          `${process.env.REACT_APP_API_URL}api/v1/posp-onboarding/exam-start/${modules?.id}/`
        );
        await getTrainingModules();
        navigate(`/app/training/${moduleId}/assessment`);
      } catch (error) {
        console.error("Error Proceeding to Assessment: ", error);
        setAlert({ type: 'error', message: error?.response?.data?.message || "Error proceeding to assessment." });
      }
    } else {
      setAlert({ type: 'warning', message: "You've already completed the Test!" });
    }
  };

  const formatTime = (sec) => {
    if (!sec || isNaN(sec)) return '0:00';
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };


  const handleLoadedMetadata = () => {

    if (videoRef.current && currentTraining && !isInitialized) {
      const dur = videoRef.current.duration;
      setVideoDuration(dur);

      const completionMins = currentTraining.completion_minutes || 0;
      const durationMins = currentTraining.duration_minutes || 1500;
      const initialProgressRatio = completionMins / durationMins;
      const initialMaxReached = initialProgressRatio * dur;

      maxReachedRef.current = initialMaxReached;

      const isCompleted = currentTraining.completion_status === "Completed" || initialProgressRatio >= 0.95;

      if (initialMaxReached > 0 && !isCompleted && initialMaxReached < dur) {
        videoRef.current.currentTime = initialMaxReached;
        setCurrentTime(initialMaxReached);
      }
      setIsInitialized(true);
    }
  };

  const handleTimeUpdate = useCallback(() => {
    const video = videoRef.current;
    if (!video || isCorrecting.current || !currentTraining) return;

    const current = video.currentTime;
    setCurrentTime(current);

    // If the user tries to seek beyond what they've watched, snap back
    if (current > maxReachedRef.current + 0.5) {
      isCorrecting.current = true;
      video.currentTime = maxReachedRef.current;
      isCorrecting.current = false;
      setAlert({ type: 'warning', message: 'You cant forward the video beyond the progress made.' });
      return;
    }

    // Advance the maximum reached marker as the video plays forward naturally
    // Only advance if the jump is small (typical playback is ~0.25s between updates)
    if (current > maxReachedRef.current && !video.seeking && (current - maxReachedRef.current) < 1.0) {
      maxReachedRef.current = current;
    }
  }, [currentTraining]);

  const handleSeeking = useCallback(() => {
    const video = videoRef.current;
    if (!video || isCorrecting.current || !currentTraining) return;

    if (video.currentTime > maxReachedRef.current + 0.5) {
      isCorrecting.current = true;
      video.currentTime = maxReachedRef.current;
      isCorrecting.current = false;
      setAlert({ type: 'warning', message: 'You cant forward the video beyond the progress made.' });
    }
  }, [currentTraining]);

  const handlePause = () => {
    if (!videoRef.current || !currentTraining || !videoDuration) return;

    const current = videoRef.current.currentTime;
    const actualProgressSeconds = Math.max(current, maxReachedRef.current);
    const progressPct = actualProgressSeconds / videoDuration;

    const durationMins = currentTraining.duration_minutes || 1500;
    const completedMinutes = Math.min(
      durationMins,
      parseFloat((progressPct * durationMins).toFixed(4))
    );

    let status = "In Progress";
    if (progressPct >= 0.95 || completedMinutes >= currentTraining.duration_minutes) {
      status = "Completed";
    } else if (completedMinutes === 0) {
      status = "Not Started";
    }

    updateProgressOnBackend(moduleId, completedMinutes, status);
  };

  const handleEnded = useCallback(() => {
    if (!currentTraining || !videoRef.current || !videoDuration) return;

    maxReachedRef.current = videoDuration;
    setCurrentTime(videoDuration);

    updateProgressOnBackend(
      moduleId,
      currentTraining.duration_minutes || 1500,
      "Completed"
    );
    setAlert({ type: 'success', message: '🎉 Congratulations! You have completed this module.' });
  }, [currentTraining, moduleId, videoDuration, updateProgressOnBackend]);

  // Save progress on unmount or tab close
  useEffect(() => {
    const saveProgress = () => {
      if (!videoRef.current || !currentTraining || !videoDuration) return;

      const current = videoRef.current.currentTime;
      const actualProgressSeconds = Math.max(current, maxReachedRef.current);
      const progressPct = actualProgressSeconds / videoDuration;

      const durationMins = currentTraining.duration_minutes || 1500;
      const completedMinutes = Math.min(
        durationMins,
        parseFloat((progressPct * durationMins).toFixed(4))
      );

      // Only save if some actual progress exists
      if (completedMinutes > 0) {
        updateProgressOnBackend(moduleId, completedMinutes);
      }
    };

    window.addEventListener('beforeunload', saveProgress);
    return () => {
      window.removeEventListener('beforeunload', saveProgress);
      saveProgress();
    };
  }, [currentTraining, moduleId, videoDuration, updateProgressOnBackend]);

  if (!modules || !modules.trainings) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 2,
          mt: 4,
        }}
      >
        <CircularProgress
          size={28}
          sx={{
            color: "#1a237e",
          }}
        />

        <Typography
          variant="body1"
          sx={{
            color: "#1a237e",
            fontWeight: 500,
          }}
        >
          Loading training module...
        </Typography>
      </Box>
    );
  }

  if (!currentTraining) {
    return (
      <Box>
        <Alert severity="error">Module not found.</Alert>
      </Box>
    );
  }

  const displayDuration = videoDuration || (currentTraining.duration_minutes * 60);

  // Progress % is based on how far they've genuinely watched (maxReached), not current playback time
  const watchedPct = videoDuration
    ? Math.min(100, Math.round((maxReachedRef.current / videoDuration) * 100))
    : (currentTraining.duration_minutes
      ? Math.min(100, Math.round((currentTraining.completion_minutes / currentTraining.duration_minutes) * 100))
      : 0);

  const isCompleted = currentTraining.completion_status === "Completed" || watchedPct >= 95;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <IconButton onClick={() => navigate('/app/training')} size="small" sx={{ mr: 0.5 }}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5" fontWeight={700}>
            {currentTraining.module_name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {currentTraining.module_description}
          </Typography>
        </Box>
        {examStatus === "In Progress" ? (
          <Chip
            icon={<Pending />}
            label={`Exam: ${examStatus}`}
            color="warning"
            size="medium"
          />
        ) : examStatus === "Completed" ? (
          <Chip
            icon={<CheckCircleIcon />}
            label={`Exam: ${examStatus}`}
            color="success"
            size="medium"
          />
        ) : (
          <Chip
            icon={<Cancel />}
            label={`Exam: ${examStatus || "Not Completed"}`}
            color="error"
            size="medium"
          />
        )}
      </Box>
      {isCompleted && (
        <Box
          sx={{
            mt: 2,
            mb: 2,
            p: 4,
            borderRadius: 3,
            background:
              "linear-gradient(135deg, rgba(79,70,229,0.08) 0%, rgba(124,58,237,0.12) 100%)",
            border: "1px solid",
            borderColor: "primary.light",
            textAlign: "center",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Decorative circles */}
          <Box
            sx={{
              position: "absolute",
              width: 180,
              height: 180,
              borderRadius: "50%",
              bgcolor: "rgba(79,70,229,0.08)",
              top: -70,
              right: -70,
            }}
          />

          <Box
            sx={{
              position: "absolute",
              width: 120,
              height: 120,
              borderRadius: "50%",
              bgcolor: "rgba(124,58,237,0.08)",
              bottom: -40,
              left: -40,
            }}
          />

          <Typography variant="h5" fontWeight={700} mb={1}>
            🎉 You're Ready for the Assessment
          </Typography>

          <Typography color="text.secondary" sx={{ mb: 3 }}>
            You've completed the training. Continue to the assessment to evaluate your
            understanding and complete this module.
          </Typography>

          <Button
            variant="contained"
            size="large"
            startIcon={<Quiz />}
            endIcon={<ArrowForward />}
            sx={{
              px: 5,
              py: 1.5,
              borderRadius: 2,
              fontWeight: 700,
              fontSize: "1rem",
              textTransform: "none",
              background: "linear-gradient(135deg, #1a237e 0%, #283593 60%, #08e2c9ff 100%)",
              boxShadow: "0 8px 20px rgba(79, 70, 229, 0.35)",
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "translateY(-1px)",
                boxShadow: "0 12px 28px rgba(79, 70, 229, 0.45)",
                background: "linear-gradient(135deg, #1a227ed5 0%, #283493d5 70%, #08e2c8da 100%)",
              },
            }}
            onClick={handleProceedToAssessment}
          >
            Proceed to Assessment
          </Button>
        </Box>
      )}

      {alert && (
        <Alert
          severity={alert.type}
          sx={{ mb: 2, borderRadius: 2 }}
          onClose={() => setAlert(null)}
        >
          {alert.message}
        </Alert>
      )}
      {getAlert && (
        <Alert
          severity={getAlert.type}
          sx={{ mb: 2, borderRadius: 2 }}
          onClose={() => setGetAlert(null)}
        >
          {getAlert.message}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
        {/* Video Player */}
        {examStatus === "Not Completed" && (
          <Box sx={{ flex: 2 }}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: '1px solid rgba(0,0,0,0.06)',
                overflow: 'hidden',
                bgcolor: '#000',
              }}
            >
              <video
                ref={videoRef}
                src={currentTraining.training_video}
                controls
                style={{ width: '100%', display: 'block', maxHeight: 420 }}
                onLoadedMetadata={handleLoadedMetadata}
                onTimeUpdate={handleTimeUpdate}
                onSeeking={handleSeeking}
                onPause={handlePause}
                onEnded={handleEnded}
                controlsList="nodownload"
              />
            </Paper>

          </Box>
        )}

        {/* Module info panel */}
        <Box sx={{ flex: 1 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid rgba(0,0,0,0.06)' }}>
            <Typography variant="subtitle1" fontWeight={700} mb={2}>
              Module Info
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  MODULE NAME
                </Typography>
                <Typography variant="body2" fontWeight={600} mt={0.3}>
                  {currentTraining.module_name}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  TOTAL DURATION
                </Typography>
                <Typography variant="body2" fontWeight={600} mt={0.3}>
                  {formatTime(displayDuration)}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  WATCHED UP TO
                </Typography>
                <Typography variant="body2" fontWeight={600} mt={0.3}>
                  {formatTime(maxReachedRef.current)}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  STATUS
                </Typography>
                <Box mt={0.3}>
                  {isCompleted ? (
                    <Chip icon={<CheckCircleIcon />} label="Completed" color="success" size="small" />
                  ) : watchedPct > 0 ? (
                    <Chip icon={<PlayCircleIcon />} label="In Progress" color="warning" size="small" />
                  ) : (
                    <Chip label="Not Started" size="small" variant="outlined" />
                  )}
                </Box>
              </Box>

              {!isCompleted && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: '1px solid #ffe082',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                  }}
                >
                  <LockIcon sx={{ color: '#f57c00', fontSize: 18, mt: 0.2 }} />
                  <Typography variant="caption" color="#f1c112ff">
                    You can freely re-watch any part you've already seen. Skipping ahead of your progress is not allowed.
                  </Typography>
                </Paper>
              )}
            </Box>
          </Paper>
        </Box>
      </Box>
      {/* Progress panel */}
      <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid rgba(0,0,0,0.06)', mt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="body2" fontWeight={700} color="text.secondary">
            Watched Progress
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {formatTime(currentTime)} / {formatTime(displayDuration)}
            </Typography>
            <Typography
              variant="body2"
              fontWeight={700}
              color={watchedPct >= 95 ? '#2e7d32' : '#1a237e'}
            >
              {watchedPct}%
            </Typography>
          </Box>
        </Box>

        <LinearProgress
          variant="determinate"
          value={watchedPct}
          sx={{
            height: 10,
            borderRadius: 5,
            bgcolor: 'rgba(26,35,126,0.1)',
            '& .MuiLinearProgress-bar': {
              borderRadius: 5,
              background: watchedPct >= 95
                ? 'linear-gradient(90deg, #2e7d32, #66bb6a)'
                : 'linear-gradient(90deg, #2c37b9ff, #00897b)',
              transition: 'transform 0.1s linear',
            },
          }}
        />

        {isCompleted && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
            <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 16 }} />
            <Typography variant="caption" color="success.main" fontWeight={600}>
              Module completed! You can re-watch any part freely.
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default TrainingModule;
